import { Link } from 'react-router-dom';

const plans = [
  {
    name: 'Free',
    price: '$0',
    period: '/month',
    description: 'Try AdForge and see what AI ad creative can do for your business.',
    primaryOutput: '2 standard ads / month',
    secondaryOutput: 'or 8 social posts',
    creditAllowance: '25 credits included',
    features: [
      '1 creative image per generation',
      'Facebook & Instagram formats',
      'Complete ad copy package',
      '2 free image edits per ad',
      'Social post generator',
    ],
    cta: 'Start Free',
    ctaLink: '/signup',
    featured: false,
    badge: null,
  },
  {
    name: 'Pro',
    price: '$29',
    period: '/month',
    description: 'For serious business owners who run ads regularly and want consistent results.',
    primaryOutput: '25 standard ads / month',
    secondaryOutput: 'or 83 social posts',
    creditAllowance: '250 credits included',
    features: [
      '3 creative images per generation',
      'All 5 ad platforms',
      'Expanded ad copy package',
      '2 free image edits per ad',
      'Editor mode',
      'Save & organize ad library',
      'Priority support',
      'Video ads',
      'Social post generator',
    ],
    cta: 'Start Pro Free',
    ctaLink: '/signup',
    featured: true,
    badge: 'Most Popular',
  },
  {
    name: 'Agency',
    price: '$99',
    period: '/month',
    description: 'For agencies and teams managing ads for multiple clients at scale.',
    primaryOutput: '100 standard ads / month',
    secondaryOutput: 'or 333 social posts',
    creditAllowance: '1,000 credits included',
    features: [
      '6 creative images per generation',
      'All 5 ad platforms',
      'Expanded ad copy package',
      '2 free image edits per ad',
      'Editor mode',
      '5 team seats included',
      'Video ads',
      'Social post generator',
    ],
    cta: 'Start Agency Free',
    ctaLink: '/signup',
    featured: false,
    badge: 'Best for Agencies',
  },
];

export default function PricingPreview() {
  return (
    <section className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="inline-block text-sm font-semibold text-orange-500 uppercase tracking-widest mb-3">Pricing</span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-stone-900 mb-4">
            Simple, transparent pricing
            <br />
            <span className="text-orange-500">that scales with you</span>
          </h2>
          <p className="text-lg text-stone-500 max-w-xl mx-auto">
            Start free. Upgrade when you need more. No contracts, cancel anytime.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl p-8 border flex flex-col ${
                plan.featured
                  ? 'bg-gradient-to-b from-orange-500 to-amber-500 border-transparent text-white'
                  : 'bg-white border-stone-200 text-stone-900'
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className={`text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap ${plan.featured ? 'bg-stone-900 text-white' : 'bg-orange-500 text-white'}`}>
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="mb-6">
                <div className={`text-xs font-bold uppercase tracking-widest mb-1 ${plan.featured ? 'text-white/70' : 'text-orange-500'}`}>
                  {plan.name}
                </div>
                <div className="flex items-end gap-1 mb-2">
                  <span className={`text-4xl font-black ${plan.featured ? 'text-white' : 'text-stone-900'}`}>{plan.price}</span>
                  <span className={`text-sm mb-1 ${plan.featured ? 'text-white/70' : 'text-stone-400'}`}>{plan.period}</span>
                </div>
                <p className={`text-sm leading-relaxed ${plan.featured ? 'text-white/80' : 'text-stone-500'}`}>{plan.description}</p>
                <div className={`mt-5 rounded-2xl border px-4 py-4 ${plan.featured ? 'border-white/20 bg-white/15' : 'border-orange-100 bg-orange-50/60'}`}>
                  <div className={`text-[11px] font-bold uppercase tracking-widest ${plan.featured ? 'text-white/70' : 'text-orange-500'}`}>
                    Monthly output
                  </div>
                  <div className={`mt-2 text-lg font-black leading-tight ${plan.featured ? 'text-white' : 'text-stone-900'}`}>
                    {plan.primaryOutput}
                  </div>
                  <div className={`mt-1 text-sm font-semibold ${plan.featured ? 'text-white/85' : 'text-stone-700'}`}>
                    {plan.secondaryOutput}
                  </div>
                  <div className={`mt-3 inline-flex rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${plan.featured ? 'bg-white/20 text-white' : 'bg-white text-stone-600 ring-1 ring-orange-100'}`}>
                    {plan.creditAllowance}
                  </div>
                </div>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <i className={`ri-check-line mt-0.5 shrink-0 ${plan.featured ? 'text-white' : 'text-orange-500'}`} />
                    <span className={plan.featured ? 'text-white/90' : 'text-stone-600'}>{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={plan.ctaLink}
                className={`block text-center py-3.5 rounded-xl text-sm font-bold transition-all duration-200 whitespace-nowrap ${
                  plan.featured
                    ? 'bg-white text-orange-600 hover:bg-orange-50'
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 text-sm font-semibold text-stone-600 hover:text-orange-500 transition-colors"
          >
            See full plan comparison
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
